/// <reference path="../types.d.ts" />
import {
  authenticationContext,
  cmxLocation,
  HttpMethods,
  ContentTypes,
  parseXCacheHeader,
  type CaseDetailCacheOutcome,
  type CaseDetailCacheRequestOptions
} from '@codametrix/ui-common';
import {
  FormSubmissionError,
  UnauthenticatedError,
  ServerError,
  PageNotFoundError,
  BadRequestError,
  resolveApiPath,
  getCsrfHandler,
  shouldAttachCsrfToken,
  shouldAttachBearerAuthorization
} from '@codametrix/client-api';
import * as AppJump from './app-jump';

const STOCK_HEADERS: KeyValuePair = {
  'Content-Type': 'application/json'
};

const handleInauthenticated = (endpoint: string) => {
  AppJump.saveJump();
  throw new UnauthenticatedError(`error contacting api ${endpoint}`);
};

const isX = (startInclusive: number, endExclusive: number) => {
  return (statusCode: number) =>
    statusCode >= startInclusive && statusCode < endExclusive;
};

const is500 = isX(500, 600);

const omitAuthorization = (headers: KeyValuePair): KeyValuePair => {
  const next = { ...headers };
  for (const key of Object.keys(next)) {
    if (key.toLowerCase() === 'authorization') {
      delete next[key];
    }
  }
  return next;
};

const withCaseDetailCacheHeaders = (
  request: FetchOptions,
  options?: CaseDetailCacheRequestOptions
): FetchOptions => {
  if (
    options?.acceptCache === undefined &&
    options?.cacheReplace !== true
  ) {
    return request;
  }
  const headers: KeyValuePair = { ...request.headers };
  if (options?.acceptCache !== undefined) {
    headers['Accept-Cache'] = options.acceptCache ? 'true' : 'false';
  }
  if (options?.cacheReplace) {
    headers['X-Cache-Replace'] = 'true';
  }
  return { ...request, headers };
};

const _fetchRequest = async (
  request: FetchOptions,
  token: string,
  useStockHeaders: boolean = true,
  userBearerTokens: boolean = false
): Promise<Response> => {
  const attachBearer = shouldAttachBearerAuthorization();
  const authHeaders =
    attachBearer && token
      ? {
          Authorization: userBearerTokens ? `Bearer ${token}` : token
        }
      : {};
  const method = request.init?.method ?? HttpMethods.GET;
  const resolvedEndpoint = resolveApiPath(request.endpoint);
  const csrfToken = getCsrfHandler().getToken();
  const csrfHeaders =
    csrfToken && shouldAttachCsrfToken(String(method), resolvedEndpoint)
      ? { 'X-CSRF-TOKEN': csrfToken }
      : {};
  let callerHeaders = { ...(request.headers ?? {}) };
  if (!attachBearer) {
    callerHeaders = omitAuthorization(callerHeaders);
  }
  const headers: KeyValuePair = {
    ...(useStockHeaders ? STOCK_HEADERS : {}),
    ...authHeaders,
    ...csrfHeaders,
    ...callerHeaders
  };
  const options: RequestInit = {
    method: HttpMethods.GET,
    cache: 'no-cache',
    headers,
    redirect: 'follow',
    referrer: 'no-referrer',
    credentials: 'include',
    ...request.init
  };
  if (request.body) {
    options.body = JSON.stringify(request.body);
  }

  const response = await fetch(resolvedEndpoint, options);

  if (!response.ok) {
    const { status } = response;
    if (is500(status)) {
      throw new ServerError(`error contacting api ${request.endpoint}`);
    } else if (status === 400) {
      const responseBody = await response.json();
      throw new BadRequestError(`error contacting api`, response, responseBody);
    }
    if (status === 403 || status === 401) {
      throw new UnauthenticatedError(
        `error contacting api ${request.endpoint}`
      );
    } else if (status === 409) {
      const responseBody = await response.json();
      throw new FormSubmissionError(
        `error contacting api`,
        response,
        responseBody
      );
    } else if (status === 404) {
      throw new PageNotFoundError(`page not found`);
    }
  }

  const location = response.headers.get(`location`);
  if (location) {
    cmxLocation.setLocation(location);
  }

  return response;
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  switch (response.headers.get('content-type')) {
    case ContentTypes.APPLICATION_CSV: {
      const blob = await response.blob();
      const contentDisposition = response.headers.get(`content-disposition`);
      return { blob, contentDisposition } as T;
    }
    case ContentTypes.APPLICATION_JSON:
    case ContentTypes.APPLICATION_JSON_UTF_8:
      return await response.json();
    default:
      return undefined as T;
  }
};

const _execRequest = async (
  request: FetchOptions,
  token: string,
  useStockHeaders: boolean = true,
  userBearerTokens: boolean = false
): Promise<any> => {
  const response = await _fetchRequest(
    request,
    token,
    useStockHeaders,
    userBearerTokens
  );
  return parseJsonResponse(response);
};

const resolveAuth = (
  request: FetchOptions,
  useBearerToken: boolean
): { token: string; userBearerTokens: boolean } => {
  if (!shouldAttachBearerAuthorization()) {
    return { token: '', userBearerTokens: false };
  }
  const token = authenticationContext.getToken();
  const bearerPrefix = 'Bearer ';
  const isBearerToken = token?.startsWith(bearerPrefix) ?? false;
  if (!token) {
    return handleInauthenticated(request.endpoint);
  }
  return {
    token,
    userBearerTokens: useBearerToken && !isBearerToken
  };
};

export const api = async <T>(
  request: FetchOptions,
  useStockHeaders: boolean = true,
  useBearerToken: boolean = false
): Promise<T> => {
  const { token, userBearerTokens } = resolveAuth(request, useBearerToken);
  return _execRequest(request, token, useStockHeaders, userBearerTokens);
};

export const apiWithCacheMeta = async <T>(
  request: FetchOptions,
  options?: CaseDetailCacheRequestOptions,
  useStockHeaders: boolean = true,
  useBearerToken: boolean = false
): Promise<{ data: T; xCache: CaseDetailCacheOutcome | undefined }> => {
  const { token, userBearerTokens } = resolveAuth(request, useBearerToken);
  const response = await _fetchRequest(
    withCaseDetailCacheHeaders(request, options),
    token,
    useStockHeaders,
    userBearerTokens
  );
  const data = await parseJsonResponse<T>(response);
  return { data, xCache: parseXCacheHeader(response) };
};
